import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { listRecycleOrderConsignmentAgreement } from 'apis/oms';
import LOCALS from 'commons/locals';
import usePagination from 'commons/hooks/usePagination';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import {
  ConsignmentAgreementListItem,
  ConsignmentAgreementSnsPlatform,
} from 'types/oms';
import formatTimeInTimezone from 'utils/formatTimeInTimezone';
import i18n from '../../../i18n';
import { CONSIGNMENT_AGREEMENT_SNS_LABELS } from '../consignment-agreement/copy';

const JAPAN_TIMEZONE = 'Asia/Tokyo';

const ConsignmentAgreementList = () => {
  const { loading, setLoading, dataSource, setDataSource, total, setTotal } =
    usePagination<ConsignmentAgreementListItem>();
  const [query, setQuery] = useQueryStates({
    name: parseAsString.withDefault(''),
    submissionId: parseAsString.withDefault(''),
    pageNum: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
  });
  const [name, setName] = useState(query.name);
  const [submissionId, setSubmissionId] = useState(query.submissionId);

  useEffect(() => {
    setName(query.name);
    setSubmissionId(query.submissionId);
  }, [query.name, query.submissionId]);

  const load = useCallback(() => {
    const parsedSubmissionId = Number(query.submissionId);
    setLoading(true);
    listRecycleOrderConsignmentAgreement({
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      name: query.name.trim() || undefined,
      submissionId:
        query.submissionId.trim() && Number.isInteger(parsedSubmissionId)
          ? parsedSubmissionId
          : undefined,
    })
      .then((res) => {
        setDataSource(res.list || []);
        setTotal(res.total || 0);
      })
      .finally(() => setLoading(false));
  }, [
    query.name,
    query.pageNum,
    query.pageSize,
    query.submissionId,
    setDataSource,
    setLoading,
    setTotal,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const search = () => {
    const nextName = name.trim();
    const nextSubmissionId = submissionId.trim();
    const unchanged =
      nextName === query.name &&
      nextSubmissionId === query.submissionId &&
      query.pageNum === 1;
    setQuery({
      name: nextName,
      submissionId: nextSubmissionId,
      pageNum: 1,
    });
    if (unchanged) load();
  };

  const reset = () => {
    setName('');
    setSubmissionId('');
    const unchanged =
      query.name === '' &&
      query.submissionId === '' &&
      query.pageNum === 1 &&
      query.pageSize === 10;
    setQuery({
      name: '',
      submissionId: '',
      pageNum: 1,
      pageSize: 10,
    });
    if (unchanged) load();
  };

  const columns: ColumnsType<ConsignmentAgreementListItem> = [
    {
      title: i18n.t(LOCALS.consignment_agreement_signed_at),
      dataIndex: 'signedAt',
      render: (value: string) => formatTimeInTimezone(value, JAPAN_TIMEZONE),
    },
    {
      title: i18n.t(LOCALS.consignment_agreement_customer),
      dataIndex: 'customerName',
      render: (value: string) => value || '-',
    },
    {
      title: i18n.t(LOCALS.consignment_agreement_bring_in_no),
      dataIndex: 'submissionId',
      render: (value: number) => (
        <Link
          to={`/rrs/recycling-consignment-list?submissionId=${value}&status=all`}
        >
          <Button type="link" className="p-0">
            {value}
          </Button>
        </Link>
      ),
    },
    {
      title: i18n.t(LOCALS.consignment_agreement_sns),
      dataIndex: 'snsPlatforms',
      render: (value: ConsignmentAgreementSnsPlatform[]) =>
        value?.length
          ? value
              .map((item) => CONSIGNMENT_AGREEMENT_SNS_LABELS[item] || item)
              .join('、')
          : '-',
    },
    {
      title: i18n.t(LOCALS.operation),
      key: 'actions',
      render: (_, record) => (
        <div className="flex items-center">
          <Link to={`/rrs/consignment-agreement/${record.submissionId}`}>
            <Button type="link">{i18n.t(LOCALS.view)}</Button>
          </Link>
          <Button
            type="link"
            onClick={() =>
              window.open(
                `/prints/consignment-agreement?submissionId=${record.submissionId}`,
              )
            }
          >
            {i18n.t(LOCALS.print)}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          className="w-44"
          allowClear
          placeholder={
            i18n.t(LOCALS.consignment_agreement_customer) ?? undefined
          }
          value={name}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={search}
        />
        <Input
          className="w-44"
          allowClear
          placeholder={
            i18n.t(LOCALS.consignment_agreement_bring_in_no) ?? undefined
          }
          value={submissionId}
          onChange={(event) => setSubmissionId(event.target.value)}
          onPressEnter={search}
        />
        <Button type="primary" onClick={search}>
          {i18n.t(LOCALS.search)}
        </Button>
        <Button onClick={reset}>{i18n.t(LOCALS.reset)}</Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: 960 }}
        pagination={{
          current: query.pageNum,
          pageSize: query.pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setQuery({ pageNum: nextPage, pageSize: nextPageSize });
          },
        }}
      />
    </div>
  );
};

export default ConsignmentAgreementList;
